import { ApplicationStatus } from '../api/types/monthlyApplication';

/** 表示要素ID定数（上からの定義順で表示位置が決まります） */
export const MONTHLY_APPLICATION_LIST_IDS = {
  contractSignedAt: 'contractSignedAt',
  appliedAt: 'appliedAt',
  requestedStartDate: 'requestedStartDate',
  scheduledStartDate: 'scheduledStartDate',
  contractStartDate: 'contractStartDate',
  contractEndDate: 'contractEndDate',
  rejectedAt: 'rejectedAt',
  closedAt: 'closedAt',
  requestedCloseDate: 'requestedCloseDate',
  scheduledCloseDate: 'scheduledCloseDate',
  garageNumber: 'garageNumber',
} as const;

export const MONTHLY_APPLICATION_LIST_BUTTON_IDS = {
  checkContractButton: 'checkContractButton',
  listPaymentHistoryButton: 'listPaymentHistoryButton',
  changeMonthlyPaymentButton: 'changeMonthlyPaymentButton',
  closeApplicationLinkButton: 'closeApplicationLinkButton',
  showGarageCertDataButton: 'showGarageCertDataButton',
} as const;

export type MonthlyApplicationListIds =
  keyof typeof MONTHLY_APPLICATION_LIST_IDS;

export type MonthlyApplicationListButtonIds =
  keyof typeof MONTHLY_APPLICATION_LIST_BUTTON_IDS;

export const MONTHLY_APPLICATION_COLUMNS = {
  appliedAt: {
    ID: MONTHLY_APPLICATION_LIST_IDS.appliedAt,
    label: '申込日',
  },
  requestedStartDate: {
    ID: MONTHLY_APPLICATION_LIST_IDS.requestedStartDate,
    label: '契約開始希望日',
  },
  scheduledStartDate: {
    ID: MONTHLY_APPLICATION_LIST_IDS.scheduledStartDate,
    label: '契約開始予定日',
  },
  contractStartDate: {
    ID: MONTHLY_APPLICATION_LIST_IDS.contractStartDate,
    label: '契約開始日',
  },
  contractEndDate: {
    ID: MONTHLY_APPLICATION_LIST_IDS.contractEndDate,
    label: '現契約満了日',
  },
  contractSignedAt: {
    ID: MONTHLY_APPLICATION_LIST_IDS.contractSignedAt,
    label: '契約締結日',
  },
  rejectedAt: {
    ID: MONTHLY_APPLICATION_LIST_IDS.rejectedAt,
    label: '審査結果通知日',
  },
  closedAt: {
    ID: MONTHLY_APPLICATION_LIST_IDS.closedAt,
    label: '解約日',
  },
  garageNumber: {
    ID: MONTHLY_APPLICATION_LIST_IDS.garageNumber,
    label: '区画',
  },
  requestedCloseDate: {
    ID: MONTHLY_APPLICATION_LIST_IDS.requestedCloseDate,
    label: '解約希望日',
  },
  scheduledCloseDate: {
    ID: MONTHLY_APPLICATION_LIST_IDS.scheduledCloseDate,
    label: '解約予定日',
  },
  checkContractButton: {
    ID: MONTHLY_APPLICATION_LIST_BUTTON_IDS.checkContractButton,
    label: '契約情報（確認・変更）',
  },
  listPaymentHistoryButton: {
    ID: MONTHLY_APPLICATION_LIST_BUTTON_IDS.listPaymentHistoryButton,
    label: '決済履歴',
  },
  changeMonthlyPaymentButton: {
    ID: MONTHLY_APPLICATION_LIST_BUTTON_IDS.changeMonthlyPaymentButton,
    label: 'お支払い方法変更',
  },
  closeApplicationLinkButton: {
    ID: MONTHLY_APPLICATION_LIST_BUTTON_IDS.closeApplicationLinkButton,
    label: '解約はこちら',
  },
  showGarageCertDataButton: {
    ID: MONTHLY_APPLICATION_LIST_BUTTON_IDS.showGarageCertDataButton,
    label: '車庫証明\nダウンロード',
  },
} as const satisfies {
  [key in MonthlyApplicationListIds | MonthlyApplicationListButtonIds]: {
    ID: MonthlyApplicationListIds | MonthlyApplicationListButtonIds;
    label: string;
  };
};

export const MONTHLY_APPLICATION_STATUS = {
  PROVISIONAL_APPLICATION_REVIEW: {
    ID: 'PROVISIONAL_APPLICATION_REVIEW',
    label: '仮申込審査中',
    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.appliedAt.ID,
      MONTHLY_APPLICATION_COLUMNS.requestedStartDate.ID,
    ],
  },
  PROVISIONAL_APPLICATION_RESUBMISSION_REQUESTED: {
    ID: 'PROVISIONAL_APPLICATION_RESUBMISSION_REQUESTED',
    label: '仮申込再提出',
    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.appliedAt.ID,
      MONTHLY_APPLICATION_COLUMNS.requestedStartDate.ID,
    ],
  },
  PROVISIONAL_APPLICATION_RE_REVIEW: {
    ID: 'PROVISIONAL_APPLICATION_RE_REVIEW',
    label: '仮申込再審査中',
    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.appliedAt.ID,
      MONTHLY_APPLICATION_COLUMNS.requestedStartDate.ID,
    ],
  },
  PROVISIONAL_APPLICATION_REJECTED: {
    ID: 'PROVISIONAL_APPLICATION_REJECTED',
    label: '仮申込却下',
    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.appliedAt.ID,
      MONTHLY_APPLICATION_COLUMNS.requestedStartDate.ID,
    ],
  },
  PROVISIONAL_APPLICATION_CANCELLED: {
    ID: 'PROVISIONAL_APPLICATION_CANCELLED',
    label: '仮申込キャンセル済み',
    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.appliedAt.ID,
      MONTHLY_APPLICATION_COLUMNS.requestedStartDate.ID,
    ],
  },
  APPLICATION_PENDING: {
    ID: 'APPLICATION_PENDING',
    label: '本申込待ち',

    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.appliedAt.ID,
      MONTHLY_APPLICATION_COLUMNS.scheduledStartDate.ID,
    ],
  },
  APPLICATION_REVIEW: {
    ID: 'APPLICATION_REVIEW',
    label: '本申込審査中',
    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.appliedAt.ID,
      MONTHLY_APPLICATION_COLUMNS.scheduledStartDate.ID,
    ],
  },
  APPLICATION_RESUBMISSION_REQUESTED: {
    ID: 'APPLICATION_RESUBMISSION_REQUESTED',
    label: '本申込再提出',
    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.appliedAt.ID,
      MONTHLY_APPLICATION_COLUMNS.scheduledStartDate.ID,
    ],
  },
  APPLICATION_INITIAL_PAYMENT_PENDING: {
    ID: 'APPLICATION_INITIAL_PAYMENT_PENDING',
    label: 'お支払い待ち',
    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.appliedAt.ID,
      MONTHLY_APPLICATION_COLUMNS.contractStartDate.ID,
      MONTHLY_APPLICATION_COLUMNS.contractEndDate.ID,
      MONTHLY_APPLICATION_COLUMNS.garageNumber.ID,
    ],
  },
  APPLICATION_CONTRACT_SIGNATURE_PENDING: {
    ID: 'APPLICATION_CONTRACT_SIGNATURE_PENDING',
    label: '署名待ち',
    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.appliedAt.ID,
      MONTHLY_APPLICATION_COLUMNS.contractStartDate.ID,
      MONTHLY_APPLICATION_COLUMNS.contractEndDate.ID,
      MONTHLY_APPLICATION_COLUMNS.garageNumber.ID,
      MONTHLY_APPLICATION_COLUMNS.checkContractButton.ID,
      MONTHLY_APPLICATION_COLUMNS.listPaymentHistoryButton.ID,
      MONTHLY_APPLICATION_COLUMNS.changeMonthlyPaymentButton.ID,
    ],
  },
  APPLICATION_CANCELLED: {
    ID: 'APPLICATION_CANCELLED',
    label: '本申込キャンセル済み',
    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.appliedAt.ID,
      MONTHLY_APPLICATION_COLUMNS.requestedStartDate.ID,
    ],
  },
  APPLICATION_REJECTED: {
    ID: 'APPLICATION_REJECTED',
    label: '本申込却下',
    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.appliedAt.ID,
      MONTHLY_APPLICATION_COLUMNS.scheduledStartDate.ID,
    ],
  },
  CONTRACT_IN_PROGRESS: {
    ID: 'CONTRACT_IN_PROGRESS',
    label: '契約中',
    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.contractSignedAt.ID,
      MONTHLY_APPLICATION_COLUMNS.contractStartDate.ID,
      MONTHLY_APPLICATION_COLUMNS.contractEndDate.ID,
      MONTHLY_APPLICATION_COLUMNS.garageNumber.ID,
      MONTHLY_APPLICATION_COLUMNS.checkContractButton.ID,
      MONTHLY_APPLICATION_COLUMNS.listPaymentHistoryButton.ID,
      MONTHLY_APPLICATION_COLUMNS.changeMonthlyPaymentButton.ID,
      MONTHLY_APPLICATION_COLUMNS.closeApplicationLinkButton.ID,
      MONTHLY_APPLICATION_COLUMNS.showGarageCertDataButton.ID,
    ],
  },
  CONTRACT_UPDATE_REQUESTED: {
    ID: 'CONTRACT_UPDATE_REQUESTED',
    label: '更新申請中',
    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.contractStartDate.ID,
      MONTHLY_APPLICATION_COLUMNS.contractEndDate.ID,
      MONTHLY_APPLICATION_COLUMNS.garageNumber.ID,
      MONTHLY_APPLICATION_COLUMNS.checkContractButton.ID,
      MONTHLY_APPLICATION_COLUMNS.listPaymentHistoryButton.ID,
      MONTHLY_APPLICATION_COLUMNS.changeMonthlyPaymentButton.ID,
      MONTHLY_APPLICATION_COLUMNS.showGarageCertDataButton.ID,
      MONTHLY_APPLICATION_COLUMNS.contractSignedAt.ID,
    ],
  },
  CONTRACT_UPDATE_RESUBMISSION_REQUESTED: {
    ID: 'CONTRACT_UPDATE_RESUBMISSION_REQUESTED',
    label: '更新再提出',
    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.contractStartDate.ID,
      MONTHLY_APPLICATION_COLUMNS.contractEndDate.ID,
      MONTHLY_APPLICATION_COLUMNS.garageNumber.ID,
      MONTHLY_APPLICATION_COLUMNS.checkContractButton.ID,
      MONTHLY_APPLICATION_COLUMNS.listPaymentHistoryButton.ID,
      MONTHLY_APPLICATION_COLUMNS.changeMonthlyPaymentButton.ID,
      MONTHLY_APPLICATION_COLUMNS.showGarageCertDataButton.ID,
      MONTHLY_APPLICATION_COLUMNS.contractSignedAt.ID,
    ],
  },
  CONTRACT_CANCELLATION_REQUESTED: {
    ID: 'CONTRACT_CANCELLATION_REQUESTED',
    label: '解約申請中',
    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.contractStartDate.ID,
      MONTHLY_APPLICATION_COLUMNS.contractEndDate.ID,
      MONTHLY_APPLICATION_COLUMNS.garageNumber.ID,
      MONTHLY_APPLICATION_COLUMNS.checkContractButton.ID,
      MONTHLY_APPLICATION_COLUMNS.listPaymentHistoryButton.ID,
      MONTHLY_APPLICATION_COLUMNS.requestedCloseDate.ID,
      MONTHLY_APPLICATION_COLUMNS.changeMonthlyPaymentButton.ID,
      MONTHLY_APPLICATION_COLUMNS.showGarageCertDataButton.ID,
      MONTHLY_APPLICATION_COLUMNS.contractSignedAt.ID,
    ],
  },
  CONTRACT_SCHEDULED_CANCELLATION: {
    ID: 'CONTRACT_SCHEDULED_CANCELLATION',
    label: '解約予定',
    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.appliedAt.ID,
      MONTHLY_APPLICATION_COLUMNS.scheduledStartDate.ID,
      MONTHLY_APPLICATION_COLUMNS.contractEndDate.ID,
      MONTHLY_APPLICATION_COLUMNS.checkContractButton.ID,
      MONTHLY_APPLICATION_COLUMNS.listPaymentHistoryButton.ID,
      MONTHLY_APPLICATION_COLUMNS.scheduledCloseDate.ID,
      MONTHLY_APPLICATION_COLUMNS.garageNumber.ID,
      MONTHLY_APPLICATION_COLUMNS.changeMonthlyPaymentButton.ID,
      MONTHLY_APPLICATION_COLUMNS.showGarageCertDataButton.ID,
    ],
  },
  CONTRACT_CLOSED: {
    ID: 'CONTRACT_CLOSED',
    label: '解約済み',
    showColumns: [
      MONTHLY_APPLICATION_COLUMNS.appliedAt.ID,
      MONTHLY_APPLICATION_COLUMNS.scheduledStartDate.ID,
      MONTHLY_APPLICATION_COLUMNS.contractEndDate.ID,
      MONTHLY_APPLICATION_COLUMNS.checkContractButton.ID,
      MONTHLY_APPLICATION_COLUMNS.listPaymentHistoryButton.ID,
      MONTHLY_APPLICATION_COLUMNS.closedAt.ID,
      MONTHLY_APPLICATION_COLUMNS.garageNumber.ID,
      MONTHLY_APPLICATION_COLUMNS.changeMonthlyPaymentButton.ID,
      MONTHLY_APPLICATION_COLUMNS.showGarageCertDataButton.ID,
    ],
  },
} as const satisfies {
  [key in ApplicationStatus]: {
    ID: ApplicationStatus;
    label: string;
    showColumns: (
      | MonthlyApplicationListIds
      | MonthlyApplicationListButtonIds
    )[];
  };
};
export const APPLICATION_LIST_GET_LIMIT = 5;

export const PRE_APPLICATION_STEP = {
  // 1.必要項目入力
  // 2.必要書類のアップロード（車両写真）
  // 3.必要書類のアップロード（免許証）
  // 4.必要書類のアップロード（車検証）
  // 5.申込内容確認
  // 6.申込完了
  GENERAL: 1,
  UPLOAD_VEHICLE: 2,
  UPLOAD_LICENSE: 3,
  UPLOAD_INSPECTION: 4,
  CONFIRMATION: 5,
  COMPLETION: 6,
} as const;

/**
 * 利用目的（個人アカウント）
 *
 * AP002: 仮申込では使用目的の選択肢を廃止し、
 * すべて「個人」として扱うため、
 * 現在は PERSONAL のみを保持する。
 */
export const usagePersonalPurposeOptions = [
  { value: 'PERSONAL', label: '個人 (自家用車)' },
];

/** 法人使用目的 */
export const CORPORATE_USAGE_PURPOSE = {
  /** 法人利用（社有車） */
  COMPANY_OWNED: 'COMPANY_OWNED',
  /** 法人利用（従業員所有車） */
  EMPLOYEE_OWNED: 'EMPLOYEE_OWNED',
} as const;

/**
 * 利用目的（法人アカウント）
 */
export const usageCorporatePurposeOptions = [
  { value: CORPORATE_USAGE_PURPOSE.COMPANY_OWNED, label: '法人利用（社有車）' },
  {
    value: CORPORATE_USAGE_PURPOSE.EMPLOYEE_OWNED,
    label: '法人利用（従業員所有車）',
  },
];

/**
 * 個人車両保有ステータス
 */
export const PERSONAL_VEHICLE_OWNERSHIP_STATUS = {
  /** 契約車両が決定している */
  CONTRACT_VEHICLE_DECIDED: 'CONTRACT_VEHICLE_DECIDED',
  /**契約車両未保有（納車前など） */
  CONTRACT_VEHICLE_UNOWNED: 'CONTRACT_VEHICLE_UNOWNED',
} as const;

/**
 * 法人車両保有ステータス（選択肢）
 */
export const personalVehicleOwnershipStatusOptions = [
  {
    value: PERSONAL_VEHICLE_OWNERSHIP_STATUS.CONTRACT_VEHICLE_DECIDED,
    label: '契約車両が決定している',
  },
  {
    value: PERSONAL_VEHICLE_OWNERSHIP_STATUS.CONTRACT_VEHICLE_UNOWNED,
    label: '契約車両未保有（納車前など）',
  },
] as const;

/**
 * 車両保有（個人仮申込 新規入力用）
 */
export const hasVehicleOptions = [
  { value: 'CONTRACT_VEHICLE_DECIDED', label: '契約車両が決定している' },
  { value: 'CONTRACT_VEHICLE_UNOWNED', label: '契約車両未保有（納車前など）' },
] as const;

/** 駐車車両種別 */
export const PARKED_VEHICLE_CATEGORY = {
  /** 自動車 */
  AUTOMOBILE: 'AUTOMOBILE',
  /** バイク */
  BIKE: 'BIKE',
  /**
   * 既存ユーザーの互換性維持（AUTOMOBILE/BIKEどちらでも場合はこちらが返ってくる）
   * ただし、新規仮申込には使えない
   */
  NOT_SPECIFIED: 'NOT_SPECIFIED',
} as const;

export const parkedVehicleCategoryOptions = [
  { value: PARKED_VEHICLE_CATEGORY.AUTOMOBILE, label: '自動車' },
  { value: PARKED_VEHICLE_CATEGORY.BIKE, label: 'バイク' },
] as const;

/** バイク排気量 */
export const BIKE_DISPLACEMENT = {
  UNDER_125CC: 'UNDER_125CC',
  FROM_126_TO_250CC: 'FROM_126_TO_250CC',
  OVER_251CC: 'OVER_251CC',
} as const;

export const bikeDisplacementOptions = [
  {
    value: BIKE_DISPLACEMENT.UNDER_125CC,
    label: '125cc以下',
  },
  {
    value: BIKE_DISPLACEMENT.FROM_126_TO_250CC,
    label: '126cc〜250cc',
  },
  {
    value: BIKE_DISPLACEMENT.OVER_251CC,
    label: '251cc以上',
  },
] as const;

/** 無職・家事従業者/その他の配列 */
export const UNEMPLOYED_JOB_TYPES = ['HOMEWORKER_OTHER'];
